'use client';

/**
 * Token freshness as a PRECONDITION of every request.
 *
 * The app used to discover expiry by failing: `supabase-js` stops its
 * auto-refresh ticker whenever the tab is hidden and runs no timer at all while
 * the machine is suspended, so a tab that survived a night sent an
 * hours-expired token, collected 401s, and only then refreshed. Making
 * freshness a precondition catches every trigger — sleep/resume, hidden tabs,
 * throttled timers, clock jumps, bfcache restore — without enumerating any of
 * them.
 *
 * The decisive consequence is WHEN the refresh happens. `auth-js` preserves the
 * session when a refresh fails while the access token is still valid, and drops
 * it once the token has already expired. Refreshing inside the margin moves
 * every refresh permanently into the survivable case.
 * (`planning/auth-session-expiry/plan.md` §2.2, §4.1.)
 */

import { getAccessToken, getAuthState, waitForAuthReady } from '@/lib/authState';
import { refreshAccessToken } from '@/lib/authTokenRefresh';
import { reportTerminalRefreshOutcome } from '@/lib/authExpiryGuard';
import {
  AuthSessionEvent,
  logAuthSessionEvent,
} from '@/lib/authSessionTelemetry';
import { AuthRefreshOutcome } from '@/types/auth-types';

/**
 * Refresh this far ahead of expiry. Matches `auth-js`'s own `EXPIRY_MARGIN_MS`
 * (3 × its 30s tick), so the precondition and the library agree on what "about
 * to expire" means and never fight over the same token.
 */
const TOKEN_EXPIRY_MARGIN_MS = 90_000;

/**
 * How long a request will wait on a refresh before going out anyway. A stalled
 * refresh (offline, Supabase unreachable) must not hang the UI — the request
 * proceeds with what it has, fails normally, and the expiry guard picks it up.
 */
const REFRESH_WAIT_TIMEOUT_MS = 2_000;

const MILLISECONDS_PER_SECOND = 1_000;

function isWithinExpiryMargin(expiresAtSeconds: number | undefined): boolean {
  // A session without an expiry claim cannot be reasoned about; leave it alone
  // and let the request tell us.
  if (expiresAtSeconds === undefined) return false;
  const expiresAtMs = expiresAtSeconds * MILLISECONDS_PER_SECOND;
  return expiresAtMs - Date.now() <= TOKEN_EXPIRY_MARGIN_MS;
}

/**
 * Run the refresh but stop waiting after `REFRESH_WAIT_TIMEOUT_MS`.
 *
 * The refresh is NOT abandoned on timeout — it stays in flight behind the
 * single-flight gate and its outcome is still routed to the session-death
 * owner, so a dead refresh token discovered here is acted on even though this
 * particular request has already gone out.
 */
function startBoundedRefresh(): Promise<void> {
  const refresh = refreshAccessToken().then((result) => {
    if (result.outcome === AuthRefreshOutcome.Terminal) {
      // The refresh token is dead while the access token still works. Left
      // unreported, the app would keep using a doomed session until some later
      // 401 tripped the breaker — and on a public surface that 401 may never
      // come. `reportTerminalRefreshOutcome` is the single sign-out owner.
      reportTerminalRefreshOutcome(result.reason);
    }
  });

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timeoutId = setTimeout(resolve, REFRESH_WAIT_TIMEOUT_MS);
  });

  return Promise.race([refresh, timeout]).finally(() => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  });
}

/**
 * The access token to send with a request, refreshed first if it is expired or
 * about to be. Returns `null` only when there is no session at all.
 *
 * A failure here degrades to "send what we have and let the request fail" — the
 * only outcome it acts on is a terminal one, and even that is handed to the
 * expiry guard rather than decided locally, so session death keeps a single
 * owner.
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  await waitForAuthReady();

  const session = getAuthState().session;
  if (!session) return null;

  if (!isWithinExpiryMargin(session.expires_at)) {
    return session.access_token;
  }

  logAuthSessionEvent({
    event: AuthSessionEvent.TokenRefreshPreemptive,
    props: { expiresAt: session.expires_at ?? null },
  });

  await startBoundedRefresh();

  // Read back through authState rather than through the refresh result: on a
  // timeout the refresh is still in flight, and on success the TOKEN_REFRESHED
  // event has already landed the new session here.
  return getAccessToken() ?? session.access_token;
}
